/**
 * Cross-origin "Script error." suppressor.
 *
 * Some preview/production hosts (Cloudflare RUM, challenge platform, third-
 * party analytics beacons) inject scripts from a different origin. When ANY
 * of those scripts throw, the browser censors the stack for CORS reasons
 * and dispatches an anonymised `error` event on `window` with:
 *     message  = "Script error."
 *     filename = ""
 *     lineno   = 0
 *     colno    = 0
 *     error    = null
 *
 * CRA's dev-time runtime overlay listens for these events and renders the
 * intimidating red "Uncaught runtime errors / Script error / handleError"
 * banner — even though the crash is in a third-party script we can neither
 * see nor fix. That overlay makes the public landing page look broken to
 * testers, so we intercept the event during the capture phase and swallow
 * it BEFORE the overlay's own listener fires. Legitimate errors from OUR
 * own code (with a real filename/lineno) fall straight through and still
 * surface in the overlay + console for debugging.
 *
 * This file must be imported at the very top of `index.js` — BEFORE any
 * React or app code — so its `window.addEventListener` is registered first
 * and therefore runs first in the capture phase.
 */

const isAnonymisedCrossOriginError = (ev) => {
  if (!ev) return false;
  const msg = (ev.message || "").toString();
  // Firefox emits "Script error." (with dot), Chrome/Safari "Script error"
  // (no dot). Match both. Also match completely empty messages which are
  // typically CORS-masked errors from third-party scripts.
  const looksAnonymised =
    msg === "Script error." ||
    msg === "Script error" ||
    msg === "" ||
    msg.trim() === "";
  if (!looksAnonymised) return false;
  // A real same-origin error will populate `filename` and `lineno` even if
  // the message is odd. Only swallow when the browser has fully censored
  // the source location — that's the tell-tale sign of a CORS-masked crash.
  return (
    (!ev.filename || ev.filename === "") &&
    (!ev.lineno || ev.lineno === 0) &&
    !ev.error
  );
};

const suppress = (ev) => {
  if (!isAnonymisedCrossOriginError(ev)) return;
  ev.preventDefault?.();
  ev.stopImmediatePropagation?.();
  // Also log to our own console so the crash isn't fully invisible to us —
  // just no longer alarming to end-users.
  console.warn(
    "[buildintel] Ignored cross-origin script error (CORS-masked, not from BuildIntel code).",
  );
};

// Capture phase so we run BEFORE the CRA runtime overlay's listener.
window.addEventListener("error", suppress, /* useCapture */ true);

// Same treatment for unhandled promise rejections whose reason is a
// stringified "Script error." — happens with some Cloudflare beacons.
window.addEventListener(
  "unhandledrejection",
  (ev) => {
    const reason =
      (ev && ev.reason && (ev.reason.message || ev.reason)) || "";
    const str = String(reason);
    if (str === "Script error." || str === "Script error") {
      ev.preventDefault?.();
      ev.stopImmediatePropagation?.();
    }
  },
  true,
);

// Cross-origin uncaught errors are actually delivered to `window.onerror`
// (the property callback the browser invokes internally) — NOT through the
// DOM event system — so `addEventListener` alone cannot intercept them
// before the CRA/WDS runtime overlay's own `window.onerror` handler fires.
// Install our own property-callback and delegate to any pre-existing one
// only when the error isn't a CORS-masked anonymised crash. This lets us
// keep every other error visible (Sentry-style dev overlay, console) while
// silencing the specific class of errors we cannot inspect or fix.
const existingOnerror = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
  const looksAnonymised =
    (message === "Script error." || message === "Script error" || message === "") &&
    (!source || source === "") &&
    (!lineno || lineno === 0) &&
    !error;
  if (looksAnonymised) {
    console.warn(
      "[buildintel] Ignored cross-origin script error (CORS-masked, not from BuildIntel code).",
    );
    return true; // returning truthy prevents the default browser handling
  }
  if (typeof existingOnerror === "function") {
    return existingOnerror.apply(this, arguments);
  }
  return false;
};
